import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './Batches.css';

function Batches() {
  const { batchId } = useParams();
  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) {
          setError('No admin authentication token found. Please log in as an admin at /admin-login.');
          return;
        }

        // Use absolute URL for debugging (revert to relative URL after proxy fix)
        const url = batchId
          ? `https://valleyapi-dr9y.onrender.com/admin/batches/${batchId}`
          : 'https://valleyapi-dr9y.onrender.com/admin/batches';
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const text = await response.text();
          let errorMessage;
          switch (response.status) {
            case 401:
              errorMessage = 'Unauthorized: Invalid or expired admin token. Please log in again at /admin-login.';
              break;
            case 404:
              errorMessage = `Batch not found: The batch ID ${batchId} does not exist or has been cleared. Try uploading a new CSV.`;
              break;
            case 500:
              errorMessage = 'Server error: Check server logs for issues in csv-upload.js or database connectivity.';
              break;
            default:
              errorMessage = `HTTP error: ${response.status}`;
          }
          throw new Error(`${errorMessage}. Response starts with: ${text.slice(0, 50)}...`);
        }

        const data = await response.json();
        const batchArray = batchId ? [data] : data;
        setBatches(batchArray);
        setFilteredBatches(batchArray);
        setError(null);
        setRetryCount(0);

        // Poll for updates if any batch is still processing
        if (batchArray.some(batch => batch.status === 'processing')) {
          setTimeout(fetchBatches, 1000);
        }
      } catch (err) {
        console.error('Fetch batches error:', err);
        if (retryCount < 3) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            fetchBatches();
          }, 2000);
        } else {
          setError(`Failed to fetch batches after retries: ${err.message}`);
        }
      }
    };

    fetchBatches();
  }, [batchId, retryCount]);

  useEffect(() => {
    const filtered = batches.filter(batch =>
      batch.filename?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBatches(filtered);
  }, [searchTerm, batches]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  if (error) {
    return (
      <div className="batch-progress-container error">
        <h1>Batch Processing Progress</h1>
        <p className="error">{error}</p>
        <button
          onClick={() => {
            setError(null);
            setRetryCount(0);
          }}
          className="retry-button"
        >
          Retry
        </button>
        <p className="error-help">
          If the issue persists:
          <ul>
            <li>Ensure the server is running at https://valleyapi-dr9y.onrender.com.</li>
            <li>Verify /admin/batches or /admin/batches/:batchId is registered in csv-upload.js and mounted at /admin in server.js.</li>
            <li>Check if package.json has "proxy": "https://valleyapi-dr9y.onrender.com".</li>
            <li>Check if you are logged in with a valid admin token (Application - Local Storage - admin_token).</li>
            <li>Inspect server logs for errors in verify-token.js or csv-upload.js.</li>
            <li>
              <a href="/admin-login" className="login-link">Log in as admin</a>
            </li>
            <li>
              <a href="/admin/products/addcsv" className="login-link">Upload a new CSV</a>
            </li>
          </ul>
        </p>
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="batch-progress-container">
        <h1>Batch Processing Progress</h1>
        <p className="no-batches">Nothing yet! Upload CSV at /admin/products/addcsv to see batches here.</p>
      </div>
    );
  }

  return (
    <div className="batch-progress-container">
      <h1>Batch Processing Progress</h1>
      {batchId ? null : (
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by CSV filename..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
      )}
      <div className="batches-list">
        {filteredBatches.map(batch => (
          <div key={batch.batchId} className="batch-item">
            <h2>Batch: {batch.filename} (ID: {batch.batchId})</h2>
            <div className="progress-details">
              <p><strong>Status:</strong> {batch.status}</p>
              <p><strong>Total Records:</strong> {batch.total}</p>
              <p><strong>Processed:</strong> {batch.processed}</p>
              <p>
                <strong>Created At:</strong>{' '}
                {batch.createdAt ? new Date(batch.createdAt).toLocaleString() : 'N/A'}
              </p>
              {batch.errors?.length > 0 && (
                <div className="errors">
                  <h3>Errors:</h3>
                  <ul>
                    {batch.errors.map((err, index) => (
                      <li key={index} className="error">{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Batches;